import { useCallback, useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import {
  UploadCloud, FileText, Trash2, RefreshCw, Search, BookOpen, Sparkles,
  Loader2, Check, AlertCircle, X, ChevronRight, HelpCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import {
  Table, TableHeader, TableRow, TableHead, TableBody, TableCell,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { api, API_BASE, errorMessage, tokenStore, orgStore } from "@/lib/api";
import { toast } from "sonner";
import { ModuleGuide } from "@/components/ModuleGuide";
import { KB_GUIDE } from "@/lib/guides";
import { SamTipCard } from "@/components/SamTipCard";
import { useSamTip } from "@/context/SamTipsContext";

function fmtSize(bytes) {
  if (!bytes) return "—";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function fmtDate(iso) {
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}

export default function KnowledgeBase() {
  const tip = useSamTip("knowledge");
  const [tab, setTab] = useState("docs");
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [openDoc, setOpenDoc] = useState(null);
  const [docChunks, setDocChunks] = useState([]);
  const fileInputRef = useRef(null);
  const pollRef = useRef(null);

  const fetchDocs = useCallback(async () => {
    try { const r = await api.get("/kb/documents"); setDocs(r.data); }
    catch (err) { toast.error(errorMessage(err)); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Poll while any doc is processing
  useEffect(() => {
    const anyProcessing = docs.some((d) => d.status === "processing");
    if (anyProcessing) {
      pollRef.current = setInterval(fetchDocs, 2500);
      return () => clearInterval(pollRef.current);
    }
    if (pollRef.current) clearInterval(pollRef.current);
  }, [docs, fetchDocs]);

  // ---- Upload ----
  const uploadFiles = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    for (const file of files) {
      if (file.size > 20 * 1024 * 1024) {
        toast.error(`${file.name} exceeds 20MB`); continue;
      }
      const fd = new FormData();
      fd.append("file", file);
      try {
        const r = await fetch(`${API_BASE}/kb/documents`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenStore.get()}`,
            "X-Org-Id": orgStore.get() || "",
          },
          body: fd,
        });
        if (!r.ok) {
          const t = await r.text();
          try { const j = JSON.parse(t); throw new Error(j.detail || "Upload failed"); }
          catch { throw new Error(t || "Upload failed"); }
        }
        const data = await r.json();
        setDocs((d) => [data, ...d]);
        toast.success(`Uploading ${file.name}…`);
      } catch (err) { toast.error(err.message); }
    }
    setUploading(false);
    fetchDocs();
  };

  const onFiles = (e) => { uploadFiles(e.target.files); e.target.value = ""; };

  const onDrop = (e) => {
    e.preventDefault(); setDragOver(false);
    uploadFiles(e.dataTransfer.files);
  };

  // ---- Toggle / delete ----
  const toggleUseInChat = async (doc, value) => {
    setDocs((d) => d.map((x) => (x.id === doc.id ? { ...x, use_in_chat: value } : x)));
    try {
      await api.patch(`/kb/documents/${doc.id}`, { use_in_chat: value });
      toast.success(value ? `${doc.filename} active in chat` : `${doc.filename} paused`);
    } catch (err) {
      toast.error(errorMessage(err));
      setDocs((d) => d.map((x) => (x.id === doc.id ? { ...x, use_in_chat: !value } : x)));
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await api.delete(`/kb/documents/${deleteTarget.id}`);
      setDocs((d) => d.filter((x) => x.id !== deleteTarget.id));
      toast.success("Document removed");
      setDeleteTarget(null);
    } catch (err) { toast.error(errorMessage(err)); setDeleteTarget(null); }
  };

  // ---- Search ----
  const runSearch = async (e) => {
    e?.preventDefault?.();
    if (!search.trim()) { setResults(null); return; }
    setSearching(true);
    try {
      const r = await api.post("/kb/search", { query: search, top_k: 8 });
      setResults(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
    finally { setSearching(false); }
  };

  // ---- View doc detail ----
  const viewDoc = async (doc) => {
    setOpenDoc(doc); setDocChunks([]);
    try {
      const r = await api.get(`/kb/documents/${doc.id}/chunks`);
      setDocChunks(r.data);
    } catch (err) { toast.error(errorMessage(err)); }
  };

  // Iter 23 (audit D2) — deep-link from Chat / Content "Sources" chips.
  // When the URL carries ?doc=<id>, auto-open the doc Sheet once the
  // KB document list has loaded. A ref guards against re-opening after
  // the user closes the Sheet (we clear the param post-open).
  const [searchParams, setSearchParams] = useSearchParams();
  const consumedDocParamRef = useRef(null);
  useEffect(() => {
    const targetId = searchParams.get("doc");
    if (!targetId || loading) return;
    if (consumedDocParamRef.current === targetId) return;
    const target = docs.find((d) => d.id === targetId);
    if (target) {
      consumedDocParamRef.current = targetId;
      viewDoc(target);
      const next = new URLSearchParams(searchParams);
      next.delete("doc");
      setSearchParams(next, { replace: true });
    }
  }, [docs, loading, searchParams, setSearchParams]);

  return (
    <div className="mx-auto max-w-6xl">
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <div className="text-eyebrow text-cyan-400" data-testid="knowledge-eyebrow">Knowledge base</div>
        <h1 className="font-display mt-2 text-4xl font-light tracking-tight sm:text-5xl">
          Ground the AI in <span className="brand-text">your data</span>.
        </h1>
        <p className="mt-2 max-w-2xl text-white/55">
          Upload PDFs, DOCX, TXT, or MD. We chunk and embed them locally. Toggle "Use in chat"
          to make those chunks available as context for AI Chat Assistant.
        </p>
      </motion.div>

      <Tabs value={tab} onValueChange={setTab} className="mt-6">
        <TabsList className="bg-white/5 text-white" data-testid="kb-tabs">
          <TabsTrigger value="docs" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="kb-tab-docs">
            <FileText className="mr-1 h-3.5 w-3.5" /> Documents
          </TabsTrigger>
          <TabsTrigger value="guide" className="data-[state=active]:bg-white data-[state=active]:text-black" data-testid="kb-tab-guide">
            <HelpCircle className="mr-1 h-3.5 w-3.5" /> Guide
          </TabsTrigger>
        </TabsList>

        <TabsContent value="docs" className="mt-4">
          <SamTipCard
            show={!loading && docs.length === 0}
            enabled={tip.enabled}
            message={tip.message}
            prefill={tip.prefill}
            testId="sam-tip-knowledge"
            module="knowledge"
          />
      {/* Upload zone */}
      <div
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
        className={`mt-8 cursor-pointer rounded-2xl border-2 border-dashed p-8 text-center transition ${
          dragOver ? "border-fuchsia-400/60 bg-fuchsia-500/5" : "border-white/10 bg-white/[0.02] hover:border-white/20"
        }`}
        onClick={() => fileInputRef.current?.click()}
        data-testid="kb-upload-zone"
      >
        <input ref={fileInputRef} type="file" multiple accept=".pdf,.docx,.txt,.md" onChange={onFiles} className="hidden" data-testid="kb-file-input" />
        <UploadCloud className="mx-auto h-9 w-9 text-cyan-400" />
        <div className="font-display mt-3 text-xl font-light">Drop files here or click to upload</div>
        <div className="mt-1 text-sm text-white/45">PDF · DOCX · TXT · MD — up to 20 MB each</div>
        {uploading && (
          <div className="mt-3 inline-flex items-center gap-2 text-xs text-white/60">
            <Loader2 className="h-3 w-3 animate-spin" /> uploading…
          </div>
        )}
      </div>

      {/* Search */}
      <form onSubmit={runSearch} className="mt-8" data-testid="kb-search-form">
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-white/40" />
          <Input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Semantic search — try 'how do we price engagements?' or 'who is on the team?'"
            className="border-white/10 bg-black/40 pl-9 pr-24 text-white placeholder:text-white/40"
            data-testid="kb-search-input"
          />
          <Button type="submit" disabled={searching || !search.trim()} className="absolute right-1.5 top-1/2 h-8 -translate-y-1/2 rounded-md bg-white px-3 text-sm text-black hover:bg-zinc-200" data-testid="kb-search-submit">
            {searching && <Loader2 className="mr-1 h-3 w-3 animate-spin" />} Search
          </Button>
        </div>
        {results && (
          <div className="mt-2 flex items-center gap-2 text-xs text-white/45">
            <span>{results.length} chunks</span>
            <button type="button" onClick={() => { setResults(null); setSearch(""); }} className="text-white/45 hover:text-white">Clear</button>
          </div>
        )}
      </form>

      {/* Results */}
      {results && (
        <div className="mt-4 space-y-3" data-testid="kb-search-results">
          {results.length === 0 ? (
            <div className="rounded-xl border border-white/5 bg-white/[0.02] p-6 text-center text-sm text-white/50">
              No relevant chunks found. Try a different query or upload more documents.
            </div>
          ) : (
            results.map((c, i) => (
              <div key={c.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-4" data-testid={`kb-result-${i}`}>
                <div className="mb-2 flex items-center justify-between text-xs text-white/55">
                  <span className="inline-flex items-center gap-1.5"><FileText className="h-3 w-3 text-fuchsia-400" /> {c.filename}</span>
                  <span className="rounded-full bg-white/5 px-2 py-0.5 font-mono">score {c.score.toFixed(3)}</span>
                </div>
                <p className="whitespace-pre-wrap text-sm leading-relaxed text-white/80 line-clamp-4">{c.text}</p>
              </div>
            ))
          )}
        </div>
      )}

      {/* Documents table */}
      <div className="mt-10">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-2xl font-light">Documents ({docs.length})</h2>
          <Button variant="outline" size="sm" onClick={fetchDocs} className="rounded-xl border-white/15 bg-white/5 text-white/80 hover:bg-white/10" data-testid="kb-refresh">
            <RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh
          </Button>
        </div>

        <div className="overflow-hidden rounded-2xl border border-white/5 bg-white/[0.02]">
          <Table>
            <TableHeader>
              <TableRow className="border-white/5 hover:bg-transparent">
                <TableHead className="text-white/40">Document</TableHead>
                <TableHead className="text-white/40">Status</TableHead>
                <TableHead className="text-white/40">Chunks</TableHead>
                <TableHead className="text-white/40">Uploaded</TableHead>
                <TableHead className="text-white/40">Use in chat</TableHead>
                <TableHead className="text-white/40"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={6} className="text-center text-white/40"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
              )}
              {!loading && docs.length === 0 && (
                <TableRow><TableCell colSpan={6} className="text-center text-white/45 py-6">No documents yet — upload your first.</TableCell></TableRow>
              )}
              {docs.map((d) => (
                <TableRow key={d.id} className="border-white/5" data-testid={`kb-doc-row-${d.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/5">
                        <FileText className="h-4 w-4 text-fuchsia-400" />
                      </div>
                      <div>
                        <button onClick={() => viewDoc(d)} className="text-left text-sm text-white hover:underline" data-testid={`kb-doc-view-${d.id}`}>
                          {d.filename}
                        </button>
                        <div className="text-xs text-white/40">{fmtSize(d.size)}</div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>
                    <StatusPill status={d.status} />
                    {d.status === "failed" && d.error && (
                      <div className="mt-1 text-xs text-red-400/80 truncate max-w-[200px]" title={d.error}>{d.error}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-white/75">{d.chunks_count}</TableCell>
                  <TableCell className="text-white/55">{fmtDate(d.created_at)}</TableCell>
                  <TableCell>
                    <Switch
                      checked={!!d.use_in_chat}
                      disabled={d.status !== "ready"}
                      onCheckedChange={(v) => toggleUseInChat(d, v)}
                      data-testid={`kb-doc-toggle-${d.id}`}
                    />
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="sm" variant="ghost" onClick={() => setDeleteTarget(d)} className="text-red-400 hover:bg-red-500/10 hover:text-red-300" data-testid={`kb-doc-delete-${d.id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>
        </TabsContent>

        <TabsContent value="guide" className="mt-4">
          <ModuleGuide
            title={KB_GUIDE.title}
            whatItIs={KB_GUIDE.whatItIs}
            capabilities={KB_GUIDE.capabilities}
            howTo={KB_GUIDE.howTo}
            ctaLabel={KB_GUIDE.cta}
            onCta={() => setTab("docs")}
          />
        </TabsContent>
      </Tabs>

      <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <AlertDialogContent className="border-white/10 bg-[#0a0a0a] text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete "{deleteTarget?.filename}"?</AlertDialogTitle>
            <AlertDialogDescription className="text-white/55">
              All chunks and embeddings will be permanently removed.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-white/15 text-white hover:bg-white/5">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-500 text-white hover:bg-red-600" data-testid="kb-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Sheet open={!!openDoc} onOpenChange={(o) => !o && setOpenDoc(null)}>
        <SheetContent className="w-full max-w-2xl overflow-y-auto border-white/10 bg-[#0a0a0a] text-white sm:max-w-2xl">
          <SheetHeader>
            <SheetTitle className="font-display text-2xl">{openDoc?.filename}</SheetTitle>
            <SheetDescription className="text-white/55">
              {openDoc?.chunks_count} chunks · {fmtSize(openDoc?.size || 0)}
            </SheetDescription>
          </SheetHeader>
          <div className="mt-6 space-y-3" data-testid="kb-doc-chunks">
            {docChunks.length === 0 ? (
              <div className="flex justify-center py-12 text-white/40"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : docChunks.map((c) => (
              <div key={c.id} className="rounded-xl border border-white/5 bg-white/[0.02] p-3">
                <div className="text-eyebrow text-white/40">Chunk #{c.chunk_index + 1}</div>
                <p className="mt-1 whitespace-pre-wrap text-sm leading-relaxed text-white/80">{c.text}</p>
              </div>
            ))}
          </div>
        </SheetContent>
      </Sheet>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    ready:      { color: "border-emerald-500/30 bg-emerald-500/10 text-emerald-300", icon: <Check className="h-3 w-3" />,        label: "Ready" },
    processing: { color: "border-amber-500/30 bg-amber-500/10 text-amber-200",     icon: <Loader2 className="h-3 w-3 animate-spin" />, label: "Processing" },
    failed:     { color: "border-red-500/30 bg-red-500/10 text-red-300",           icon: <AlertCircle className="h-3 w-3" />,    label: "Failed" },
  };
  const s = map[status] || map.processing;
  return (
    <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs ${s.color}`}>
      {s.icon} {s.label}
    </span>
  );
}
